import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Form } from 'antd';

import {
  GenericDataFormContext,
  LOADING_SAVED_QUOTATIONS_STATES,
  QUOTATION_BUTTON,
  QuotationButton,
  QuotationInfoContext,
  QuotationStep,
  StepOneFormContext,
} from '../../../../../shared';

import {
  DECISION_TYPE,
  GetCommertialsConditionsResponse,
  QuotationCoverage,
} from '../../../../../../../../../shared/interfaces';

import { StepFourFormContext } from './StepFourFormContext';
import { StepThreeFormContext } from '../../../StepThree/contexts/StepThreeForm';
import { StepTwoFormContext } from '../../../StepTwo/contexts/StepTwoForm';
import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm';
import QuotationService, { DEFAULT_PAYMENT_PLAN_CODE } from '../../../../../../../../../shared/services/quotation';

import {
  FireQuotationComponent,
  FireQuotationRequest,
  PropertyFeature,
  WebFireQuotation,
} from '../../../../../../../../../shared/interfaces/services/fire-quotation-service';
import {
  generateInputID,
  getCoverageAndActivityKeyByID,
} from '../../../../../AP/Steps/StepThree/components/Quotation/utils';
import { getValueDefaultPaymentMethod } from '../../../../../utils/get-value-default-payment-method';
import { FireQuotationFormByTab } from '../../components';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../../shared/interfaces/loading-renewal-quotation-states-enum';
import { LoadQuotationToRenewResponse } from '../../../../../shared/interfaces/load-renewal-quotation-response-interface';
import { scrollToEmitButton } from '../../../../../../../../../shared/utils';

export interface FireQuotationForm {
  [key: string]: string;
}

export interface FirePaymentMethodForm {
  [key: string]: string | number;
  paymentMethod: string;
  comission: number;
  extraVariable: number;
  variation: number;
}

export const StepFourFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    allLocalitiesPerProvince,
    paymentMethodsList,
    allCoveragesPerPlan,
    setCanShowSpinner,
    setPlansSelectedByTabs,
    updateFormsAfterRemoveTabs,
    setTabsUserCanNotDelete,
    removedTabIndexes, 
    setRemovedTabIndexes,
    formValues,
  } = useContext(GenericDataFormContext);
  const {
    currentStep,
    quotationInfo,
    savedQuotation,
    loadSaveQuotationState,
    setLoadSaveQuotationState,
    setCurrentStep,
    quotationToRenewal,
    setRenewalQuotationState,
    renewalQuotationState
  } = useContext(QuotationInfoContext);
  const { clientSelected } = useContext(StepOneFormContext);
  const { rubricList, handleTabErrors } = useContext(WizardDataFormContext);
  const { stepThreeForm, tabs, allFeatures
  } = useContext(StepThreeFormContext);
  const { stepTwoForm } = useContext(StepTwoFormContext);

  const [commertialConditions, setCommertialConditions] =
    useState<GetCommertialsConditionsResponse>();
  const [fireQuotationComponents, setFireQuotationComponents] = useState<
    Array<FireQuotationComponent>
  >([]);
  const [quotationResponse, setQuotationResponse] =
    useState<WebFireQuotation>();
  const [labelButton, setLabelButton] = useState<QuotationButton>(
    quotationResponse ? QUOTATION_BUTTON.RECOTIZAR : QUOTATION_BUTTON.COTIZAR
  );

  const [stepFourFireQuotationForm] = Form.useForm<FireQuotationForm>();
  const [firePaymentMethodsForm] = Form.useForm<FirePaymentMethodForm>();

  const [quotationTabs, setQuotationTabs] = useState<Array<any>>([]);
  const [activeTab, setActiveTab] = useState(1);

  const generateCoveragesInfo = useCallback(
    (tabKey: string): Array<QuotationCoverage> => {
      const quotationCoverages: Array<QuotationCoverage> = [];

      const allFields = stepFourFireQuotationForm.getFieldsValue();

      const allCheckboxFields = Object.keys(allFields).filter((el) =>
        el.includes('checkbox')
      );

      const allCheckboxFieldByTab = allCheckboxFields.filter((el) =>
        el.includes(`fire-tab-${tabKey}`)
      );

      allCheckboxFieldByTab.forEach((el) => {
        if (stepFourFireQuotationForm.getFieldValue(el)) {
          const { id, activityKey } = getCoverageAndActivityKeyByID(el);

          const planSelected = stepFourFireQuotationForm?.getFieldValue(
            `plan-tab-${tabKey}`
          );

          const allCoveragesByTab = allCoveragesPerPlan?.find(
            (el) => String(el.plan as string) === String(planSelected)
          )?.coverages;

          const coverage = allCoveragesByTab?.find((el) => el.id === id);

          const sumaAseguradaInputName = generateInputID(id, activityKey);

          const primaInputName = generateInputID(id, activityKey, 'prima');
          
          quotationCoverages.push({
            cobertura: coverage?.codigo!,
            riesgo: coverage?.codigoRiesgo!,
            sumaAsegurada: stepFourFireQuotationForm.getFieldValue(
              sumaAseguradaInputName
            ),
            prima:
              stepFourFireQuotationForm.getFieldValue(primaInputName) ?? '0',
            pormilaje: '0'
          });
        }
      });

      return quotationCoverages;
    },
    [stepFourFireQuotationForm, allCoveragesPerPlan]
  );

  const generateFeaturesByTab = useCallback(
    (tabKey: number): Array<PropertyFeature> => {
      const features =
        allFeatures.find((el) => el.keyTab === tabKey)?.features ?? [];
      
      const featuresByTab: Array<PropertyFeature> = features.map((el) => ({
        aplica: stepThreeForm?.getFieldValue(
          `feature-tab-${tabKey}-${el.codigo}`
        ) ? DECISION_TYPE.YES : DECISION_TYPE.NO,
        codigo: el.codigo,
        codigoEquivalente: el.codigoEquivalente,
        descripcion: el.descripcion,
        incluyeWeb: DECISION_TYPE.YES,
        // tiene: fireFormValues.find((e: any) => e.name === `feature-tab-${tabKey}-${el.codigo}`).value
        tiene: stepThreeForm?.getFieldValue(
          `feature-tab-${tabKey}-${el.codigo}`
        )
          ? DECISION_TYPE.YES
          : DECISION_TYPE.NO,
      }));

      return featuresByTab;
    },
    [allFeatures, stepThreeForm]
  );

  const generateFireComponentsInfo =
    useCallback((): Array<FireQuotationComponent> => {
      let components: Array<FireQuotationComponent> = [];

      const categorySelected = stepTwoForm?.getFieldValue('category');

      const rubricSelected = rubricList?.actividadesComercio.find(
        (el) => String(`${el.capituloTarifa}${el.capituloTarifaInciso1}${el.capituloTarifaInciso2}`) === String(categorySelected)
      );
      
      tabs.forEach(({ key }: any) => {
        const location: string = stepThreeForm?.getFieldValue(
          `location-tab-${key}`
        );
        const provinceCode: string = stepThreeForm?.getFieldValue(
          `province-tab-${key}`
        );

        const localitiesOfProvince = allLocalitiesPerProvince.find(
          (el) => el.provinceCode === provinceCode
        )?.localities;

        const riskZone =
          localitiesOfProvince?.find(
            (el) => `${el.sufijoCodigoPostal}${el.codigoPostal}` === location
          )?.zonaRiesgosVarios ?? '';

        const featuresByTab: Array<PropertyFeature> =
          generateFeaturesByTab(key);

        const component: FireQuotationComponent = {
          nroComponente: key,
          codPlan: stepThreeForm?.getFieldValue(`plan-tab-${key}`) as number,
          tipoVivienda: 0, //* es 0 para polizas nuevas.
          caracteristicasBien: featuresByTab,
          cantCaracteristicas: featuresByTab.length,
          sufijoCodigoPostal: location[0],
          codigoPostal: location.slice(1),
          zonaRiesgo: riskZone,
          buenResultado: 0, //* es 0 para polizas nuevas.
          cobertura: generateCoveragesInfo(key),
          psua: 0, //* es 0 para polizas nuevas.
          capTarifa: rubricSelected?.capituloTarifa!,
          capTarifaInc1: rubricSelected?.capituloTarifaInciso1!,
          capTarifaInc2: rubricSelected?.capituloTarifaInciso2!,
          clasificacionRiesgo: 'A',
        };

        components.push(component);
      });

      setFireQuotationComponents(components);

      return components;
    }, [
      tabs,
      stepTwoForm,
      rubricList,
      allLocalitiesPerProvince,
      stepThreeForm,
      generateCoveragesInfo,
      generateFeaturesByTab,
      setFireQuotationComponents,
    ]);

  const generateFireQuotationForm = useCallback((): FireQuotationRequest => {
    const components = generateFireComponentsInfo();
    const paymentMethodCode = firePaymentMethodsForm.getFieldInstance('paymentMethod') ? firePaymentMethodsForm?.getFieldValue('paymentMethod').split('-')[1] : getValueDefaultPaymentMethod(paymentMethodsList)
    const administrativeSurcharge = firePaymentMethodsForm.getFieldInstance('extraVariable') ? firePaymentMethodsForm?.getFieldValue('extraVariable') : commertialConditions?.porcentajeExtraPrimaVariable

    return {
      token: quotationInfo?.token!,
      arse: quotationInfo?.arse!,
      rama: quotationInfo?.branch!,
      codigoTipoFormaPago: paymentMethodCode,
      cantComponentes: components.length,
      cliente: clientSelected!,
      componentes: components,
      recargoAdministrativo: administrativeSurcharge
    };
  }, [
    quotationInfo,
    clientSelected,
    generateFireComponentsInfo,
    firePaymentMethodsForm,
    commertialConditions,
  ]);

  const clearPrimaFieldsOnFormChange = useCallback(() => {
    const allFields = stepFourFireQuotationForm.getFieldsValue();
    const allPrimaKeys = Object.keys(allFields).filter((el) =>
      el.includes('prima')
    );

    stepFourFireQuotationForm.resetFields(allPrimaKeys);
  }, [stepFourFireQuotationForm]);

  const getCommertialsConditions = useCallback(async () => {
    try {
      if (!commertialConditions) {
        const response = await QuotationService.getComertialsConditions(
          quotationInfo?.branch!
        );

        if (response.data) setCommertialConditions(response.data);
      }
    } catch (error) { }
  }, [quotationInfo, setCommertialConditions]);


  const createTabs = useCallback(() => {
    const newTabs = tabs?.map(({ key, label }: any) => ({
      children: <FireQuotationFormByTab tabKey={key} />,
      forceRender: true,
      closable: false,
      key,
      label,
    }));

    setQuotationTabs(newTabs);

    setPlansSelectedByTabs(tabs, stepThreeForm);
  }, [tabs, setQuotationTabs, setPlansSelectedByTabs, stepThreeForm]);

  
  const updateFieldsBeforeQuotation = useCallback(
    ({ componentes }: WebFireQuotation) => {
      const paymentMethodCode =
        firePaymentMethodsForm.getFieldValue('paymentMethod');
      const defaultPaymentMethod = paymentMethodsList?.find(
        (el) => el.codigoFormaDePago === DEFAULT_PAYMENT_PLAN_CODE
      );

      if (
        paymentMethodCode &&
        paymentMethodsList?.find((el) => `${el.codigoFormaDePago}-${el.nroPlanDePago}` === paymentMethodCode)
      ) {
        firePaymentMethodsForm.setFieldValue(
          'paymentMethod',
          paymentMethodCode
        );
      } else {
        firePaymentMethodsForm.setFieldValue(
          'paymentMethod',
          `${defaultPaymentMethod?.codigoFormaDePago}-${defaultPaymentMethod?.nroPlanDePago}`
        );
      }

      if (
        !firePaymentMethodsForm.getFieldValue('commission') &&
        !firePaymentMethodsForm.getFieldValue('extraVariable')
      ) {
        firePaymentMethodsForm.setFieldValue(
          'commission',
          Number(commertialConditions?.porcentajeDeComision)
        );
        firePaymentMethodsForm.setFieldValue(
          'extraVariable',
          Number(commertialConditions?.porcentajeExtraPrimaVariable)
        );
      }

      componentes.forEach(({ nroDeComponente, coberturas }) => {
        coberturas.forEach(({ prima, id }) => {
          const primaInputName = generateInputID(
            id!,
            `fire-tab-${nroDeComponente}`,
            'prima'
          );

          stepFourFireQuotationForm.setFieldValue(
            primaInputName,
            Number(prima)
          );
        });
      });
    },
    [
      firePaymentMethodsForm,
      paymentMethodsList,
      commertialConditions,
      stepFourFireQuotationForm,
      allCoveragesPerPlan,
    ]
  );

  const handlerOnClickQuotation = useCallback(
    async (form: FireQuotationRequest) => {
      setCanShowSpinner(true);

      try {
        await stepFourFireQuotationForm.validateFields();

        const response = await QuotationService.generateFireQuotation(form);

        if (response.data) {
          scrollToEmitButton();
          setLabelButton(QUOTATION_BUTTON.EMITIR);

          const tabsUserCanNotDelete: Array<number> = [];

          //recover ID from original coverate and set it into response
          response.data.cotizacionWebIncendio.componentes.forEach(
            ({ nroDeComponente, coberturas }) => {
              const planSelected = stepFourFireQuotationForm.getFieldValue(
                `plan-tab-${nroDeComponente}`
              );

              const coveragesPerPlan =
                allCoveragesPerPlan.find(
                  (el) => (el.plan as string) === String(planSelected)
                )?.coverages ?? [];

              coberturas?.forEach((coverage) => {
                const originalCoverage = coveragesPerPlan.find(
                  (item) =>
                    item.codigo === coverage.codigoCobertura &&
                    item.codigoRiesgo === coverage.codigoRiesgo
                );
                coverage.id = originalCoverage?.id;
                coverage.subCategoriaDeTextos = originalCoverage?.subCategoriaDeTextos;
              });
              tabsUserCanNotDelete.push(Number(nroDeComponente));
            }
          );

          setTabsUserCanNotDelete(tabsUserCanNotDelete);
          setQuotationResponse(response.data.cotizacionWebIncendio);
          updateFieldsBeforeQuotation(response.data.cotizacionWebIncendio);
          return;
        };
      } catch (error: any) {
        if (error && error?.errorFields?.length > 0) {
          handleTabErrors(error?.errorFields, ['tab'], 'Incendio Web');
        }
      } finally {
        setCanShowSpinner(false);
      }
    },
    [
      stepFourFireQuotationForm,
      setLabelButton,
      setCanShowSpinner,
      setQuotationResponse,
      updateFieldsBeforeQuotation,
      setTabsUserCanNotDelete,
    ]
  );

  const handlerOnClickEmit = useCallback(async () => {
    setCurrentStep(QuotationStep.FIVE);
  }, [setCurrentStep]);

  const handlerOnClick = useCallback(async () => {
    try {
      const form: FireQuotationRequest = generateFireQuotationForm();

      if (labelButton === QUOTATION_BUTTON.EMITIR)
        return await handlerOnClickEmit();

      if (labelButton === QUOTATION_BUTTON.COTIZAR || labelButton === QUOTATION_BUTTON.RECOTIZAR)
        return await handlerOnClickQuotation(form);
    } catch (error) {}
  }, [
    labelButton,
    generateFireQuotationForm,
    handlerOnClickEmit,
    handlerOnClickQuotation,
  ]);

  const fillStepFourServices = useCallback(async () => {
    try {
      createTabs();
      await getCommertialsConditions();
    } catch (error) {}
  }, [
    getCommertialsConditions,
    createTabs,
  ]);

  const fillStepFourData = useCallback(async (isRenewal: boolean) => {
    let quotationToProccess: LoadQuotationToRenewResponse;
      if(isRenewal){
        quotationToProccess = quotationToRenewal;
      }else{
        quotationToProccess = savedQuotation;
      }
    //* Se setea vacio ya que no tenemos la primera respuesta de la COTIZACIÓN en un retome. 
    setQuotationResponse({
      sumaAsegurada: "",
      premio: "",
      prima: "",
      componentes: quotationToProccess.bienes.map((bien: any) => {

        return {
          nroDeComponente: bien.numeroComponente,
          producto: "",
          tipoVivienda: "",
          codigoPostal: "",
          sufijoCodigoPostal: "",
          zonaRiesgo: "",
          buenResultado: "",
          sumaAsegurada: "",
          premio: "",
          requiereInspeccion: "",
          porcentajeSumaAsegurada: "",
          capituloTarifa: "",
          capituloTarifaInc1: "",
          agravamiento: "",
          caracteristicas: isRenewal ? [] : savedQuotation.caracteristicasSeleccionadas,
          coberturas: isRenewal ? bien.coberturasSeleccionadas : savedQuotation.coberturasSeleccionadas,
          impuestos: [
            {
              codigoCobertura: "",
              impComision: "",
              impExtraPrimaFija: "",
              impImpuestosInternos: "",
              impIva: "",
              impIvaPercepcion: "",
              impIvaRnl: "",
              impIibbPercepcion: "",
              impIibbRiesgo: "",
              impIibbEmpresa: "",
              porImpuestosInternos: "",
              porIva: "",
              porIvaRnl: "",
              porIvaPercepcion: "",
              porTasaSsn: "",
              porServiciosSociales: "",
              impExtraPrimaVar: "",
              impRecargoFinanciero: "",
              selladoDeEmpresa: "",
              selladoDelRiesgo: "",
              impServiciosSociales: "",
              impTasaSsn: "",
              porComision: "",
              porExtraPrimaVar: "",
              porRecargoFinanciero: "",
              calculoImpuestos: ""
            }
          ],
          bonificaciones: []
        }
      }),
      impuestos: {
        rama: "1",
        cantPolizaXRama: "",
        prima: "",
        porRecargoFinanciero: "",
        impRecargoFinanciero: "",
        impExtraPrimaFija: "",
        impSubtotalPrima: "",
        selladoDelRiesgo: "",
        selladoDeEmpresa: "",
        porImpuestosInternos: "",
        impImpuestosInternos: "",
        porServiciosSociales: "",
        impServiciosSociales: "",
        porTasaSsn: "",
        impTasaSsn: "",
        impIibbPercepcion: "",
        impIibbEmpresa: "",
        impIibbRiesgo: "",
        impIvaRnl: "",
        porIvaRnl: "",
        impIva: "",
        porIva: ""
      },
      condicionesComerciales: {
        cantPolizaXRama: "",
        porRecargoAdminist: "",
        impRecargoAdminist: "",
        porComisionProd: "",
        impComision: "",
        porRecargoAdminist1: "",
        porComisionProd1: ""
      },
      condicionesComerciales1: {
        rama: "1",
        porcRecargoAdminist: ""
      }
    });

    quotationToProccess.bienes.forEach((bien: any, index:number) => {
      const _tab = index + 1;

      bien.coberturasSeleccionadas.forEach((cobertura: any) => {
        stepFourFireQuotationForm.setFields([
          { name: `checkbox_${cobertura.codigo}-${cobertura.codigoRiesgo}_fire-tab-${_tab}`, value: true },
          { name: `prima_${cobertura.codigo}-${cobertura.codigoRiesgo}_fire-tab-${_tab}`, value: cobertura.prima },
          { name: `sumaAsegurada_${cobertura.codigo}-${cobertura.codigoRiesgo}_fire-tab-${_tab}`, value: cobertura.sumaAsegurada },
        ]);
  
      });
    });

    firePaymentMethodsForm.setFields([
      { name: `paymentMethod`, value: `${quotationToProccess.formaDePago.codigo}-${quotationToProccess.formaDePago.codigoDePlanDePagos}` },
      { name: `commission`, value: quotationToProccess.formaDePago.porcentajeComision },
      { name: `extraVariable`, value: quotationToProccess.formaDePago.porcentajeExtraPrimaVariable },
      { name: `variation`, value: Number(quotationToProccess.formaDePago.porcentajeComision) - commertialConditions!.porcentajeDeComision }
    ]);

    setLabelButton(QUOTATION_BUTTON.RECOTIZAR);

  }, [
    savedQuotation,  
    quotationToRenewal,
    commertialConditions,
    setQuotationResponse,
    stepFourFireQuotationForm,
    firePaymentMethodsForm,
    setLabelButton,
  ]);

  useEffect(() => {
    if(removedTabIndexes.length && currentStep === QuotationStep.FOUR && formValues) {
      updateFormsAfterRemoveTabs(removedTabIndexes, [stepFourFireQuotationForm], formValues);
      setRemovedTabIndexes([]);
    };
  }, [removedTabIndexes, currentStep, formValues]);

  useEffect(() => {
    if (currentStep === QuotationStep.FOUR && !loadSaveQuotationState) {
      getCommertialsConditions();
    }
  }, [currentStep, loadSaveQuotationState, getCommertialsConditions]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_SERVICES_LOADING);
      fillStepFourServices();
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_SERVICES_LOADING &&
      commertialConditions
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_SERVICES_LOADED);
    }
  }, [
    loadSaveQuotationState,
    commertialConditions,
  ]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_SERVICES_LOADED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_DATA_FILLING);
      fillStepFourData(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_DATA_FILLING &&
      quotationTabs.length === tabs.length &&
      labelButton === QUOTATION_BUTTON.RECOTIZAR &&
      allCoveragesPerPlan.length > 0
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_DATA_FILLED);
    }
  }, [
    loadSaveQuotationState,
    quotationTabs,
    tabs,
    labelButton,
    allCoveragesPerPlan,
  ]);

  useEffect(() => {
    if(loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.FINISH_LOADING) {
      handlerOnClick()
    };
  }, [
    loadSaveQuotationState,
  ]);

  /**
   * Renewal
   */

  useEffect(() => {
    if (currentStep === QuotationStep.FOUR && !renewalQuotationState) {
      getCommertialsConditions();
    }
  }, [currentStep, renewalQuotationState, getCommertialsConditions]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_SERVICES_LOADING_RENEWAL);
      fillStepFourServices();
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_SERVICES_LOADING_RENEWAL &&
      commertialConditions
    ) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_SERVICES_LOADED_RENEWAL);
    }
  }, [
    renewalQuotationState,
    commertialConditions,
  ]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_SERVICES_LOADED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_DATA_FILLING_RENEWAL);
      fillStepFourData(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_DATA_FILLING_RENEWAL &&
      quotationTabs.length === tabs.length &&
      labelButton === QUOTATION_BUTTON.RECOTIZAR &&
      allCoveragesPerPlan.length > 0
    ) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_DATA_FILLED_RENEWAL);
    }
  }, [
    renewalQuotationState,
    quotationTabs,
    tabs,
    labelButton,
    allCoveragesPerPlan,
  ]);

  useEffect(() => {
    if(renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.FINISH_LOADING_RENEWAL) {
      handlerOnClick()
    };
  }, [
    renewalQuotationState,
  ]);

  const values = useMemo(
    () => ({
      clearPrimaFieldsOnFormChange,
      commertialConditions,
      firePaymentMethodsForm,
      fireQuotationComponents,
      generateFireQuotationForm,
      labelButton,
      quotationResponse,
      setLabelButton,
      setQuotationResponse,
      stepFourFireQuotationForm,
      quotationTabs,
      setQuotationTabs,
      activeTab,
      setActiveTab,
      createTabs,
      handlerOnClick,
    }),
    [
      clearPrimaFieldsOnFormChange,
      commertialConditions,
      firePaymentMethodsForm,
      fireQuotationComponents,
      generateFireQuotationForm,
      labelButton,
      quotationResponse,
      setLabelButton,
      setQuotationResponse,
      stepFourFireQuotationForm,
      quotationTabs,
      setQuotationTabs,
      activeTab,
      setActiveTab,
      createTabs,
      handlerOnClick,
    ]
  );

  return (
    <StepFourFormContext.Provider value={values}>
      {children}
    </StepFourFormContext.Provider>
  );
};
